import * as Sentry from "@sentry/react";
import { captureCustomEvent } from "./sentryUtils";
import { getNetworkInfo, getNetworkSpeedCategory, getBasicNetworkInfo } from "./networkSpeed";

/**
 * Test function to verify Sentry is working correctly
 * Call this function in the browser console or add a button to trigger it
 */
export const testSentryIntegration = async () => {
  console.log("Testing Sentry integration...");
  
  // Test 1: Basic Sentry event
  Sentry.captureMessage("Sentry test message", "info");
  
  // Test 2: Network speed test
  console.log("Testing network speed detection...");
  try {
    const networkInfo = await getNetworkInfo();
    console.log("Network info:", networkInfo);
    
    // Test 3: Custom event with network data
    await captureCustomEvent(
      "sentry_integration_test_with_network", 
      "info", 
      { 
        test_type: "manual_test_with_network",
        test_timestamp: Date.now(),
        network_test_results: networkInfo
      },
      {
        test: true,
        component: "test_utility",
        network_speed: getNetworkSpeedCategory(networkInfo)
      }
    );
  } catch (error) {
    console.error("Network test failed:", error);
  }
  
  // Test 4: Simulate an error
  try {
    throw new Error("Test error for Sentry");
  } catch (error) {
    Sentry.captureException(error);
  }
  
  console.log("Sentry test events sent! Check your Sentry dashboard.");
};

/**
 * Test network speed detection only
 */
export const testNetworkSpeed = async () => {
  console.log("🧪 Testing network speed detection...");
  
  // Test basic detection first
  console.log("1️⃣ Testing basic detection...");
  const basicInfo = getBasicNetworkInfo();
  console.log("Basic info result:", basicInfo);
  
  // Test full detection
  console.log("2️⃣ Testing full detection...");
  try {
    const networkInfo = await getNetworkInfo();
    const speedCategory = getNetworkSpeedCategory(networkInfo);
    
    console.log("✅ Network Information:", {
      connectionType: networkInfo.connection_type,
      downlinkMbps: networkInfo.downlink_mbps,
      rttMs: networkInfo.rtt_ms,
      measuredSpeedMbps: networkInfo.measured_speed_mbps,
      speedCategory: speedCategory,
      onlineStatus: networkInfo.online_status,
      detectionMethod: networkInfo.detection_method,
      errors: networkInfo.detection_errors
    });
    
    return networkInfo;
  } catch (error) {
    console.error("❌ Network speed test failed:", error);
    console.log("🔧 Fallback to basic info:", basicInfo);
    return basicInfo;
  }
};

/**
 * Debug network capabilities
 */
export const debugNetworkCapabilities = () => {
  console.log("🔍 Debugging network capabilities...");
  
  const capabilities = {
    navigator_online: navigator.onLine,
    has_connection: !!(navigator.connection || navigator.mozConnection || navigator.webkitConnection),
    connection_object: navigator.connection || navigator.mozConnection || navigator.webkitConnection,
    has_performance: !!window.performance,
    user_agent: navigator.userAgent,
    protocol: window.location.protocol,
    hostname: window.location.hostname
  };
  
  console.table(capabilities);
  return capabilities;
};

// Make it available globally for testing
if (typeof window !== 'undefined') {
  window.testSentryIntegration = testSentryIntegration;
  window.testNetworkSpeed = testNetworkSpeed;
  window.debugNetworkCapabilities = debugNetworkCapabilities;
}
